<script setup lang="ts">
import { computed, PropType } from 'vue';
import { IonItem, IonList } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import useUtils from '@/shared/composables/useUtils';
import type { TransactionItem } from '@/shared/interfaces/transaction';

const props = defineProps({
	items: {
		type: Array as PropType<TransactionItem[]>,
		required: true,
	},
	totalPrice: {
		type: Number,
		required: true,
	},
	walletReceives: {
		type: Number,
		required: false,
		default: undefined,
	},
	walletReceivesCurrency: {
		type: String,
		required: false,
		default: '€',
	},
	title: {
		type: String,
		required: true,
	},
	totalTitle: {
		type: String,
		required: true,
	},
	variant: {
		type: String,
		default: 'dark',
	},
});

const { t } = useI18n();
const { convertToCurrency } = useUtils();

const hasTopUpFee = computed(() => props.walletReceives !== undefined && props.walletReceives < props.totalPrice);

const isDark = computed(() => props.variant === 'dark');
</script>

<template>
	<ion-list :class="isDark ? 'backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4' : 'bg-white rounded-xl mb-4'">
		<div :class="isDark ? 'flex px-6 py-4 items-center text-white' : 'flex px-6 py-4 items-center text-dark-blue'">
			<span class="font-sourcesanspro-semibold text-l2">{{ title }}</span>
		</div>

		<div :class="isDark ? 'border-y border-y-white/5' : 'border-y border-y-slate-200'">
			<ion-item v-for="(item, idx) in items" :key="idx" lines="none" :class="['ion-no-padding font-sourcesanspro-regular', isDark ? 'text-white' : 'text-dark-blue/70']">
				<div class="flex px-6 py-3 w-full justify-between">
					<div>
						<div class="text-base">{{ item.description }}</div>
					</div>
					<div class="text-right">
						<span class="text-sm">
							{{ item.currency || '€' }}
							{{ convertToCurrency(item.itemPrice) }}
						</span>
					</div>
				</div>
			</ion-item>
		</div>

		<div v-if="hasTopUpFee" :class="isDark ? 'border-b border-b-white/5' : 'border-b border-b-slate-200'">
			<div :class="['flex px-6 py-3 w-full justify-between', isDark ? 'text-white/70' : 'text-dark-blue/70']">
				<div>
					<div class="text-base font-sourcesanspro-semibold">{{ t('topup.wallet_receives') }}</div>
				</div>
				<div class="text-right">
					<span class="text-sm font-sourcesanspro-semibold"> {{ `${walletReceivesCurrency}${convertToCurrency(walletReceives)}` }} </span>
				</div>
			</div>
		</div>

		<div :class="['flex px-6 py-4 items-center justify-between', isDark ? 'text-white' : 'text-dark-blue']">
			<span class="font-sourcesanspro-semibold text-l2">{{ totalTitle }}</span>
			<span class="font-sourcesanspro-semibold text-l2">
				{{ `€${convertToCurrency(totalPrice)}` }}
			</span>
		</div>
	</ion-list>
</template>

<style scoped>
ion-item {
	--background: transparent;
	--inner-padding-end: 0;
	--min-height: 40px;
}
</style>
